import type {
  HeldCatalogueItem,
  HeldRegistration,
  PricedBasket,
  RecordedSale,
  SaleRecordOutcome,
  SaleRefusalCode,
} from '@repo/desktop-bridge';
import { priceBasket } from '@repo/desktop-bridge';

/**
 * One basket across the outlets this till holds (#284): one total, one
 * payment, and **a sale per outlet**.
 *
 * A till registered at Fish Spa and Glass Bridge holds two complete, isolated
 * tills — a database, a lease, counters and a queue each — and the API reads
 * the outlet off the lease. So a visitor buying both is two sales, each written
 * into its own outlet's services, each with its own receipt and invoice number
 * and its own slip. What this adds is that they are rung up, quoted and paid
 * for once.
 *
 * Kept apart from the screen because it is the money and the failure: each
 * outlet's invoice is priced and rounded **on its own**, as each will be
 * recorded, and the recording is N writes to N files that cannot be one
 * transaction — so it goes one outlet at a time and stops at the first that
 * does not record, saying exactly where.
 */

/** Where a line is sold: `null` is the outlet the counter is on; otherwise one that joined. */
export type LineOutlet = HeldRegistration | null;

/**
 * The outlets Sell for offers: every one but a shop known to sell no admission
 * (#313). One not yet told what it sells stays, and joining it says so.
 */
export const offeredForSale = (outlets: readonly HeldRegistration[]): HeldRegistration[] =>
  outlets.filter((outlet) => outlet.sellsAdmission !== false);

export interface OutletLine {
  readonly item: HeldCatalogueItem;
  readonly quantity: number;
  /**
   * The outlet it was priced by and is sold at. `item` is that outlet's own
   * held row — price, price row and GST pair — never the counter's.
   */
  readonly outlet: LineOutlet;
}

/**
 * A basket line's identity: the outlet *and* the item. One item can be on sale
 * at two outlets, at two prices, and the two are two lines on two invoices.
 */
export const lineKey = (outlet: LineOutlet, itemId: string): string =>
  `${outlet === null ? 'here' : String(outlet.id)}:${itemId}`;

export interface OutletGroup<L extends OutletLine = OutletLine> {
  readonly outlet: LineOutlet;
  readonly lines: readonly L[];
  /**
   * This outlet's invoice, priced by `priceBasket` — the function its sale is
   * recorded with, so this is what it charges. `null` past what a number holds.
   */
  readonly priced: PricedBasket | null;
}

/**
 * The basket by outlet: **the counter's own first**, then the rest in the order
 * they were first rung up.
 *
 * Its own first because it is the one outlet nothing was checked against in
 * advance — every other one passed `joinOutlet` — so a refusal there comes
 * before anything is spent anywhere.
 */
export function byOutlet<L extends OutletLine>(lines: readonly L[]): OutletGroup<L>[] {
  const groups = new Map<number | null, L[]>();
  const ordered = [
    ...lines.filter((line) => line.outlet === null),
    ...lines.filter((line) => line.outlet !== null),
  ];

  for (const line of ordered) {
    const id = line.outlet?.id ?? null;

    groups.set(id, [...(groups.get(id) ?? []), line]);
  }

  return [...groups.values()].map((group) => ({
    outlet: group[0]?.outlet ?? null,
    lines: group,
    priced: priceBasket(group.map(({ item, quantity }) => ({ quantity, price: item }))),
  }));
}

/**
 * The number the cashier quotes: **each outlet's invoice, rounded on its own,
 * added** — never the whole basket rounded once, which can differ by a rupee
 * from what the invoices charge between them. Integer paise. `null` where any
 * outlet's cannot be priced, which its sale would refuse.
 */
export const grandTotalPaise = (groups: readonly OutletGroup[]): number | null =>
  groups.reduce<number | null>(
    (sum, group) => (sum === null || group.priced === null ? null : sum + group.priced.totalPaise),
    0,
  );

/** One sale, at the outlet it was recorded at. */
export interface OutletSale {
  readonly outlet: LineOutlet;
  readonly sale: RecordedSale;
}

export interface OutletRecording<L extends OutletLine> {
  /** What recorded on this press, in the order it did. */
  readonly recorded: readonly OutletSale[];
  /**
   * The outlet it stopped at and why, or `null` when every outlet recorded. A
   * `null` reason is a till that did not answer.
   */
  readonly stopped: { readonly outlet: LineOutlet; readonly reason: SaleRefusalCode | null } | null;
  /**
   * Every line of every outlet that did not record — **what Try again sends,
   * and nothing else**, so an outlet that recorded can never be recorded twice.
   */
  readonly remaining: readonly L[];
}

/**
 * Record each outlet's sale in turn, and stop at the first that does not.
 *
 * Stopping rather than carrying on is what keeps the failure readable: what
 * recorded is a prefix, what did not is the rest, and the cashier is told one
 * reason. Carrying on past a refusal would record outlets after a gap and leave
 * the visitor's one payment covering a scatter.
 *
 * **A call that throws recorded nothing at that outlet.** `recordSale` spends
 * the numbers and queues the write in one transaction and answers a printer
 * failure as a fact rather than throwing, so a rejection is one that never
 * committed — the screen's single-sale path reads it the same way.
 */
export async function recordByOutlet<L extends OutletLine>(
  groups: readonly OutletGroup<L>[],
  record: (group: OutletGroup<L>) => Promise<SaleRecordOutcome>,
): Promise<OutletRecording<L>> {
  const recorded: OutletSale[] = [];

  for (const [index, group] of groups.entries()) {
    let reason: SaleRefusalCode | null;

    try {
      const outcome = await record(group);

      if (outcome.recorded) {
        recorded.push({ outlet: group.outlet, sale: outcome.sale });
        continue;
      }

      reason = outcome.reason;
    } catch (error) {
      console.error('till: a sale in a basket across outlets could not be recorded', error);
      reason = null;
    }

    return {
      recorded,
      stopped: { outlet: group.outlet, reason },
      remaining: groups.slice(index).flatMap((each) => each.lines),
    };
  }

  return { recorded, stopped: null, remaining: [] };
}
